"use client"

import React, { useEffect, useState } from "react"
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  LockKeyhole,
  Plus,
} from "lucide-react"

import { Badge } from "@/shadcn/components/ui/badge"
import { Button } from "@/shadcn/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card"
import { Separator } from "@/shadcn/components/ui/separator"
import { Switch } from "@/shadcn/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@/shadcn/components/ui/toggle-group"
import { cn } from "@/lib/utils"

import DeleteAccountCard from "./account/DeleteAccountCard"
import EmailCard from "./account/EmailCard"
import PageHeading from "./account/PageHeading"
import PasswordCard from "./account/PasswordCard"
import ProfileCard from "./account/ProfileCard"
import {
  CARD,
  CARD_DESCRIPTION,
  CARD_HEADER,
  CARD_TITLE,
  SM_BUTTON,
} from "./account/styles"

interface SocialLogin {
  ls_id: number;
  serviceID: number;
  userID: string;
  userName: string;
}

export default function AccountPage(props: any) {
  const [newsOn, setNewsOn] = useState(false)
  const [alertsOn, setAlertsOn] = useState(true)
  const [statsOn, setStatsOn] = useState(true)
  const [activityRecap, setActivityRecap] = useState<
    "off" | "daily" | "weekly"
  >("weekly")

  useEffect(() => {
    // the edited copy, so a change shows at once and goes back if it doesn't save
    let constants = props.app.config.constants,
        mailOpts = (props.draft ?? props.user.detail).mailOpts;

    setNewsOn((mailOpts & constants.MAIL_INFO) ? true : false);
    setAlertsOn((mailOpts & constants.MAIL_ALERT) ? true : false);
    setStatsOn((mailOpts & constants.MAIL_STATS) ? true : false);

    if (!(mailOpts & constants.MAIL_ACTIVITY_DAILY) && !(mailOpts & constants.MAIL_ACTIVITY_WEEKLY)) {
      setActivityRecap("off")
    } else if (mailOpts & constants.MAIL_ACTIVITY_DAILY) {
      setActivityRecap("daily")
    } else if (mailOpts & constants.MAIL_ACTIVITY_WEEKLY) {
      setActivityRecap("weekly")
    }

  }, [(props.draft ?? props.user.detail).mailOpts]);

  const isFieldInvalid = (field: string): Boolean => {
    if (props.errors) {
      if (props.errors && props.errors.length) {
        for (let i = 0; i < props.errors.length; ++i) {
          if (props.errors[i].errors?.validation?.[field]) {
            return true;
          }
        }
      }
    }
    return false;
  };


  return (
    <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:flex-col tw:gap-4 tw:p-4 tw:font-sans tw:md:p-6">
      <PageHeading
        title="Your Account"
        description="Manage dlvr.it account settings."
      />

      <ProfileCard {...props} appName={props.appName} />

      <EmailCard
        email={props.user.detail.email}
        isEditing={props.isEditing.email}
        onEdit={() => {
          props.onBeginEdit('email');
        }}
        onChange={props.onUpdateField}
        onSave={() => {
          props.updateUser();
        }}
        onCancel={() => {
          props.onCancelEdit('email');
        }}
        onVerify={() => {
          props.resendEmailConfirmation();
        }}
        app={props.app}
        emailActive={props.user.detail.emailActive}
        bounceCount={props.user.detail.bounceCount}
        isFieldInvalid={isFieldInvalid}
      />

      <PasswordCard
        editing={props.isEditing.password}
        onStartEdit={() => {
          props.onBeginEdit('password');
        }}
        onSave={() => {
          props.updatePassword();
        }}
        onCancel={() => {
          props.onCancelEdit('password');
        }}
        app={props.app}
        updatePasswordValue={props.updatePasswordValue}
        isFieldInvalid={isFieldInvalid}
      />

      <SocialAccountsCard
        constants={props.app.config.constants}
        socialLogins={props.socialLogins}
        addSocialLogin={props.addSocialLogin}
        removeSocialLogin={props.removeSocialLogin}
      />

      <MasterPostingCard
        active={props.user.detail.pauseButton === 0 ? true : false}
        onChange={props.togglePause}
      />

      <NotificationsCard
        news={newsOn}
        alerts={alertsOn}
        stats={statsOn}
        activityRecap={activityRecap}
        onActivityRecapChange={(recap) => {
          setActivityRecap(recap);
          props.onActivityCadenceUpdate(recap);
        }}
        onNotificationChange={(field, newState) => {
          props.onMailOptionsUpdate(field, newState);
        }}
      />

      <ApiAccessCard
        apiKeys={props.user.detail.apiKeys}
        onGenerate={() =>
          props.generateApiKey()
        }
      />

      <DeleteAccountCard
        onDelete={() => {
          props.deleteAccount();
        }}
      />
    </div>
  )
}

function SocialAccountsCard({
  constants,
  socialLogins,
  addSocialLogin,
  removeSocialLogin
} : {
  constants: any,
  socialLogins: SocialLogin[],
  addSocialLogin: () => void,
  removeSocialLogin: () => void
}) {
  const googleLogins: Array<SocialLogin> = []
  const twitterLogins: Array<SocialLogin> = []
  const facebookLogins: Array<SocialLogin> = [];

  socialLogins.map((login) => {
    if (login.serviceID === constants.LOGIN_SVC_GOOGLE) {
      googleLogins.push(login)
    } else if (login.serviceID === constants.LOGIN_SVC_FACEBOOK) {
      facebookLogins.push(login)
    } else if (login.serviceID === constants.LOGIN_SVC_TWITTER) {
      twitterLogins.push(login)
    }
  })

  return (
    <Card className={CARD}>
      <CardHeader className={CARD_HEADER}>
        <CardTitle className={CARD_TITLE}>Login using social account</CardTitle>
        <CardDescription className={CARD_DESCRIPTION}>
          Sign in faster by linking a social account.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-2.5 tw:px-4 tw:pb-4">
        <Separator />
        {googleLogins.length ? (
          <SocialConnected
            icon={<GoogleIcon />}
            iconRadius="tw:rounded-[6px]"
            name="Google"
            accounts={googleLogins}
            addSocialLogin={addSocialLogin}
            removeSocialLogin={removeSocialLogin}
            serviceID={constants.LOGIN_SVC_GOOGLE}
          />
        ) : (
          <SocialEmpty
            icon={<GoogleIcon />}
            iconRadius="tw:rounded-sm"
            name="Google"
            addSocialLogin={addSocialLogin}
            serviceID={constants.LOGIN_SVC_GOOGLE}
          />
        )}
        <Separator />
        {facebookLogins.length ? (
          <SocialConnected
            icon={<FacebookIcon />}
            iconRadius="tw:rounded-[6px]"
            name="Facebook"
            accounts={facebookLogins}
            addSocialLogin={addSocialLogin}
            removeSocialLogin={removeSocialLogin}
            serviceID={constants.LOGIN_SVC_FACEBOOK}
          />
        ) : (
          <SocialEmpty
            icon={<FacebookIcon />}
            iconRadius="tw:rounded-sm"
            name="Facebook"
            addSocialLogin={addSocialLogin}
            serviceID={constants.LOGIN_SVC_FACEBOOK}
          />
        )}
        <Separator />
        {twitterLogins.length ? (
          <SocialConnected
            icon={<XIcon />}
            iconRadius="tw:rounded-[6px]"
            name="X"
            accounts={twitterLogins}
            addSocialLogin={addSocialLogin}
            removeSocialLogin={removeSocialLogin}
            serviceID={constants.LOGIN_SVC_TWITTER}
          />
        ) : (
          <SocialEmpty
            icon={<XIcon />}
            iconRadius="tw:rounded-sm"
            name="X"
            addSocialLogin={addSocialLogin}
            serviceID={constants.LOGIN_SVC_TWITTER}
          />
        )}
      </CardContent>
    </Card>
  )
}

function SocialConnected({
  icon,
  iconRadius,
  name,
  accounts,
  addSocialLogin,
  removeSocialLogin,
  serviceID,
}: {
  icon: React.ReactNode
  iconRadius: string
  name: string
  accounts: SocialLogin[]
  addSocialLogin: () => void
  removeSocialLogin: () => void
  serviceID: number
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <div className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:px-3 tw:pt-2.5">
        <div
          className={cn(
            "tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:border tw:border-border tw:bg-muted",
            iconRadius
          )}
        >
          {icon}
        </div>
        <p className="tw:text-sm tw:font-medium tw:leading-4 tw:text-foreground">
          {name}
        </p>
        <Button
          type="button"
          variant="outline"
          className="tw:size-6 tw:shrink-0 tw:rounded-full tw:p-0 tw:gap-0 tw:shadow-none"
          data-serviceid={serviceID}
          onClick={addSocialLogin}
          aria-label={`Add another ${name} account`}
        >
          <Plus className="tw:size-3.5" aria-hidden />
        </Button>
      </div>
      {accounts.map((a, i) => (
        <div
          key={i}
          className="tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:px-3 tw:py-1"
        >
          <p className="tw:flex-1 tw:truncate tw:text-sm tw:font-normal tw:leading-4 tw:text-muted-foreground">
            {a.userName}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(SM_BUTTON, "tw:shrink-0")}
            data-loginid={a.ls_id}
            onClick={removeSocialLogin}
            aria-label={`Disconnect ${a.userName}`}
          >
            Disconnect
          </Button>
        </div>
      ))}
    </div>
  )
}

function SocialEmpty({
  icon,
  iconRadius,
  name,
  addSocialLogin,
  serviceID,
}: {
  icon: React.ReactNode
  iconRadius: string
  name: string
  addSocialLogin: () => void
  serviceID: number
}) {
  return (
    <div className="tw:flex tw:items-start tw:gap-2 tw:rounded-lg tw:px-3 tw:py-2.5">
      <div
        className={cn(
          "tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:border tw:border-border tw:bg-muted",
          iconRadius
        )}
      >
        {icon}
      </div>
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:justify-center tw:gap-1">
        <p className="tw:text-sm tw:font-medium tw:leading-4 tw:text-foreground">
          {name}
        </p>
        <p className="tw:truncate tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground">
          Not connected
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(SM_BUTTON, "tw:shrink-0 tw:self-center")}
        data-serviceid={serviceID}
        onClick={addSocialLogin}
        aria-label={`Connect ${name}`}
      >
        Connect
      </Button>
    </div>
  )
}

function MasterPostingCard({
  active,
  onChange,
}: {
  active: boolean
  onChange: (v: boolean) => void
}) {
  let label = "Posting active",
       description = "All feeds are publishing on schedule";

  if (!active) {
    label = "Posting inactive";
    description = "We won’t look for new items or publish any posts until you resume.";
  }

  return (
    <Card className={CARD}>
      <CardHeader className={CARD_HEADER}>
        <CardTitle className={CARD_TITLE}>Master posting control</CardTitle>
        <CardDescription className={CARD_DESCRIPTION}>
          Pause all scheduled posts across every feed and destination.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:pb-4">
        <Separator />
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <p className="tw:text-sm tw:font-semibold tw:leading-5 tw:text-foreground">
              {label}
            </p>
            <p className="tw:text-sm tw:font-medium tw:leading-5 tw:text-muted-foreground">
              {description}
            </p>
          </div>
          <Switch
            checked={active}
            onCheckedChange={onChange}
            aria-label="Master posting"
          />
        </div>
      </CardContent>
    </Card>
  )
}

function NotificationsCard({
  news,
  alerts,
  stats,
  activityRecap,
  onActivityRecapChange,
  onNotificationChange,
}: {
  news: boolean
  alerts: boolean
  stats: boolean
  activityRecap: "off" | "daily" | "weekly"
  onActivityRecapChange: (v: "off" | "daily" | "weekly") => void
  onNotificationChange: (field: string, newState: boolean) => void
}) {
  return (
    <Card className={CARD}>
      <CardHeader className={CARD_HEADER}>
        <CardTitle className={CARD_TITLE}>Email notifications</CardTitle>
        <CardDescription className={CARD_DESCRIPTION}>
          Choose what you want to hear about.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:pb-4">
        <Separator />
        <NotifyRow
          title="dlvr.it news"
          description="New features, tips, and product updates."
          checked={news}
          onChange={(nextState) => {
            onNotificationChange('info', nextState)
          }}
        />
        <Separator />
        <NotifyRow
          title="Service alerts"
          badge="Recommended"
          description="Receive alerts if there are any issues with your account."
          checked={alerts}
          onChange={(nextState) => {
            onNotificationChange('alert', nextState)
          }}
        />
        <Separator />
        <NotifyRow
          title="Stats emails"
          description="Weekly summary of how your content is performing."
          checked={stats}
          onChange={(nextState) => {
            onNotificationChange('stats', nextState)
          }}
        />
        <Separator />
        <ActivityRecapRow
          value={activityRecap}
          onChange={onActivityRecapChange}
        />
      </CardContent>
    </Card>
  )
}

function NotifyRow({
  title,
  description,
  badge,
  checked,
  onChange,
}: {
  title: string
  description: string
  badge?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
        <div className="tw:flex tw:items-center tw:gap-2">
          <p className="tw:text-sm tw:font-semibold tw:leading-5 tw:text-foreground">
            {title}
          </p>
          {badge ? (
            <Badge
              variant="secondary"
              className="tw:rounded-full tw:bg-accent tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:leading-4 tw:text-secondary-foreground"
            >
              {badge}
            </Badge>
          ) : null}
        </div>
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          aria-label={title}
        />
      </div>
      <p className="tw:text-sm tw:font-medium tw:leading-5 tw:text-muted-foreground">
        {description}
      </p>
    </div>
  )
}

function ActivityRecapRow({
  value,
  onChange,
}: {
  value: "off" | "daily" | "weekly"
  onChange: (v: "off" | "daily" | "weekly") => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
        <p className="tw:text-sm tw:font-semibold tw:leading-5 tw:text-foreground">
          Activity Recap
        </p>
        <ToggleGroup
          type="single"
          value={value}
          onValueChange={(v) => {
            if (v) onChange(v as "off" | "daily" | "weekly")
          }}
          className="tw:h-8 tw:w-[152px]"
        >
          <ToggleGroupItem
            value="off"
            className="tw:h-7 tw:rounded-l-md tw:rounded-r-none tw:border tw:border-input tw:px-2 tw:text-xs tw:font-medium tw:leading-4"
          >
            Off
          </ToggleGroupItem>
          <ToggleGroupItem
            value="daily"
            className="tw:h-7 tw:rounded-none tw:border tw:border-l-0 tw:border-input tw:px-2 tw:text-xs tw:font-medium tw:leading-4"
          >
            Daily
          </ToggleGroupItem>
          <ToggleGroupItem
            value="weekly"
            className="tw:h-7 tw:rounded-l-none tw:rounded-r-md tw:border tw:border-l-0 tw:border-input tw:px-2 tw:text-xs tw:font-medium tw:leading-4"
          >
            Weekly
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <p className="tw:text-sm tw:font-medium tw:leading-5 tw:text-muted-foreground">
        A summary of your posting activity and engagement.
      </p>
    </div>
  )
}

function ApiAccessCard({
  apiKeys,
  onGenerate,
}: {
  apiKeys: Array<string>
  onGenerate: () => void
}) {
  const [revealed, setRevealed] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const filled = apiKeys && apiKeys[0]

  async function copyKey() {
    if (!apiKeys[0]) return
    try {
      await navigator.clipboard.writeText(apiKeys[0])
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch {
      /* clipboard may be unavailable */
    }
  }

  return (
    <Card className={CARD}>
      <CardHeader className={CARD_HEADER}>
        <CardTitle className={CARD_TITLE}>API access</CardTitle>
        <CardDescription className={CARD_DESCRIPTION}>
          Use this key to authenticate API requests.{" "}
          <a
            href="https://api.dlvrit.com/1/help"
            target="_blank"
            rel="noreferrer"
            className="tw:underline tw:underline-offset-2"
          >
            View docs →
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:pb-4">
        <div className="tw:flex tw:h-8 tw:w-full tw:items-center tw:gap-3.5 tw:rounded-lg tw:border tw:border-input tw:bg-background tw:px-2.5 tw:py-1">
          <LockKeyhole
            className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
            aria-hidden
          />
          {filled ? (
            <>
              <span className="tw:flex-1 tw:truncate tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground tw:select-all">
                {revealed ? apiKeys[0] : "•".repeat(28)}
              </span>
              <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? "Hide API key" : "Show API key"}
                className="tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:text-muted-foreground"
              >
                {revealed ? (
                  <EyeOff className="tw:size-4" aria-hidden />
                ) : (
                  <Eye className="tw:size-4" aria-hidden />
                )}
              </button>
              <button
                type="button"
                onClick={copyKey}
                aria-label="Copy API key"
                className="tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:text-muted-foreground"
              >
                {isCopied ? (
                  <Check className="tw:size-4" aria-hidden />
                ) : (
                  <Copy className="tw:size-4" aria-hidden />
                )}

              </button>
            </>
          ) : (
            <>
              <span className="tw:flex-1 tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground tw:select-none" />
              <span className="tw:size-5 tw:shrink-0" />
            </>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            SM_BUTTON,
            "tw:self-end",
            filled ? "tw:w-fit" : "tw:w-[97px]"
          )}
          onClick={onGenerate}
        >
          {filled ? "Regenerate key" : "Generate key"}
        </Button>
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Brand icons (inline SVGs — Figma uses brand-original colors)               */
/* -------------------------------------------------------------------------- */

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden role="img">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.44c-.28 1.43-1.1 2.65-2.36 3.47v2.85h3.83c2.24-2.07 3.58-5.12 3.58-8.56z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.83-2.85c-1.06.71-2.41 1.13-4.11 1.13-3.16 0-5.84-2.13-6.79-5h-3.96v3.13C3.27 21.31 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.21 14.36a7.18 7.18 0 010-4.71V6.52H1.25a12 12 0 000 10.96l3.96-3.12z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.81l3.4-3.4C17.95 1.19 15.24 0 12 0 7.31 0 3.27 2.69 1.25 6.52l3.96 3.13c.95-2.87 3.63-5 6.79-5z"
      />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden role="img">
      <path
        fill="#1877F2"
        d="M24 12c0-6.63-5.37-12-12-12S0 5.37 0 12c0 5.99 4.39 10.95 10.13 11.85V15.47H7.08V12h3.05V9.36c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.96h-1.52c-1.49 0-1.96.93-1.96 1.88V12h3.33l-.53 3.47h-2.8v8.38C19.61 22.95 24 17.99 24 12z"
      />
      <path
        fill="#FFFFFF"
        d="M16.67 15.47L17.2 12h-3.33V9.76c0-.95.47-1.88 1.96-1.88h1.52V4.92s-1.38-.23-2.69-.23c-2.74 0-4.53 1.66-4.53 4.67V12H7.08v3.47h3.05v8.38c.61.1 1.24.15 1.87.15s1.26-.05 1.87-.15v-8.38h2.8z"
      />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden role="img">
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"
      />
    </svg>
  )
}
